import { createContext, useContext } from 'react';

/**
 * Where a dropdown menu opens relative to its trigger.
 *
 *   'auto' (default)  below the trigger; flips above only when it is cramped
 *   'top'             above the trigger; flips below only when there is no room above
 *
 * A surface that wants every menu inside it to open upward (a dialog whose
 * body must not grow or scroll when a menu opens) wraps itself in
 * <MenuPlacementProvider value="top">. Dropdowns read it with
 * useMenuPlacement(), so nothing else in the app changes.
 */
const MenuPlacementContext = createContext('auto');
export const MenuPlacementProvider = MenuPlacementContext.Provider;
export const useMenuPlacement = () => useContext(MenuPlacementContext);

/**
 * Position a `position: fixed` menu against its trigger, inside the viewport.
 * Pure: viewport coordinates in, viewport coordinates out.
 *
 * Returns { left, top?, bottom?, maxHeight, up }: exactly one of `top` /
 * `bottom` is set, so a short menu that opens upward stays glued to the
 * trigger instead of floating at the top of the space above it.
 */
export function computeMenuPlacement(rect, {
  prefer = 'auto',
  width,
  maxHeight = 280,
  minHeight = 180,
  floor = 96,
  gap = 4,
  margin = 8,
  viewport,
} = {}) {
  const vw = viewport?.width ?? (typeof window !== 'undefined' ? window.innerWidth : 1024);
  const vh = viewport?.height ?? (typeof window !== 'undefined' ? window.innerHeight : 768);
  const w = Math.min(width || rect.width, vw - 2 * margin);
  const below = vh - rect.bottom - gap - margin;
  const above = rect.top - gap - margin;
  const need = Math.min(minHeight, maxHeight);
  const up = prefer === 'top' ? (above >= need || above > below) : (below < need && above > below);
  const space = up ? above : below;
  return {
    left: Math.min(Math.max(margin, rect.left), Math.max(margin, vw - w - margin)),
    top: up ? undefined : rect.bottom + gap,
    bottom: up ? vh - rect.top + gap : undefined,
    width: w,
    maxHeight: Math.max(floor, Math.min(maxHeight, space)),
    up,
  };
}
